import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { getLicenseDeviceIdentity } from '@/lib/licenseDevice';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from '@/components/ui/use-toast';

export type LicenseActivation = {
  id: string;
  license_id: string;
  user_id: string;
  device_fingerprint: string;
  device_name: string;
  last_seen_at: string;
  revoked_at: string | null;
  created_at: string;
};

export type LicenseActivationLicense = {
  id: string;
  sku: string;
  edition: string;
  version: string;
  device_limit: number;
  active_device_count: number;
  activations: LicenseActivation[];
};

type ActivationPayload = {
  licenses: LicenseActivationLicense[];
  primary_license_id: string | null;
  current_device_activation_id: string | null;
  current_device_activated: boolean;
  limit_reached: boolean;
  message: string | null;
};

type UseLicenseActivationsOptions = {
  autoActivate?: boolean;
};

const DEFAULT_STATE: ActivationPayload = {
  licenses: [],
  primary_license_id: null,
  current_device_activation_id: null,
  current_device_activated: false,
  limit_reached: false,
  message: null,
};

export function useLicenseActivations(options: UseLicenseActivationsOptions = {}) {
  const { autoActivate = false } = options;
  const { user, session } = useAuth();
  const [state, setState] = useState<ActivationPayload>(DEFAULT_STATE);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  const callManager = useCallback(async (body: Record<string, unknown>) => {
    const identity = await getLicenseDeviceIdentity();
    const { data, error } = await supabase.functions.invoke('manage-license-activations', {
      body: {
        ...body,
        deviceFingerprint: identity.deviceFingerprint,
        ...(body.action === 'activate' ? { deviceName: identity.deviceName } : {}),
      },
      headers: {
        Authorization: `Bearer ${session?.access_token || ''}`,
      },
    });

    if (error) {
      const message = typeof data?.message === 'string'
        ? data.message
        : error.message || 'Unable to manage device activations.';
      const nextState = (data ?? DEFAULT_STATE) as ActivationPayload;
      setState({ ...DEFAULT_STATE, ...nextState, message });
      throw new Error(message);
    }

    const nextState = { ...DEFAULT_STATE, ...(data ?? {}) } as ActivationPayload;
    setState(nextState);
    return nextState;
  }, [session?.access_token]);

  const refresh = useCallback(async () => {
    if (!user || !session?.access_token) {
      setState(DEFAULT_STATE);
      return DEFAULT_STATE;
    }

    setLoading(true);
    try {
      return await callManager({ action: 'list' });
    } catch (error: any) {
      toast({
        title: 'Unable to load device activations',
        description: error.message || 'Please try again.',
        variant: 'destructive',
      });
      return DEFAULT_STATE;
    } finally {
      setLoading(false);
    }
  }, [callManager, session?.access_token, user]);

  const activateCurrentDevice = useCallback(async (showToastOnSuccess = false) => {
    if (!user || !session?.access_token) {
      return DEFAULT_STATE;
    }

    setActionLoading(true);
    try {
      const nextState = await callManager({ action: 'activate' });

      if (nextState.current_device_activated) {
        if (showToastOnSuccess && nextState.message) {
          toast({ title: 'Device activated', description: nextState.message });
        }
      } else if (!autoActivate && nextState.message) {
        toast({
          title: 'Could not activate this device',
          description: nextState.message,
          variant: 'destructive',
        });
      }

      return nextState;
    } catch (error: any) {
      if (!autoActivate) {
        toast({
          title: 'Could not activate this device',
          description: error.message || 'Please try again.',
          variant: 'destructive',
        });
      }
      return DEFAULT_STATE;
    } finally {
      setActionLoading(false);
    }
  }, [autoActivate, callManager, session?.access_token, user]);

  const revokeActivation = useCallback(async (activationId: string) => {
    if (!user || !session?.access_token) {
      return DEFAULT_STATE;
    }

    setRevokingId(activationId);
    try {
      const nextState = await callManager({ action: 'revoke', activationId });
      toast({
        title: 'Device revoked',
        description: nextState.message || 'The device activation has been revoked.',
      });
      return nextState;
    } catch (error: any) {
      toast({
        title: 'Could not revoke device',
        description: error.message || 'Please try again.',
        variant: 'destructive',
      });
      return DEFAULT_STATE;
    } finally {
      setRevokingId(null);
    }
  }, [callManager, session?.access_token, user]);

  useEffect(() => {
    if (!user || !session?.access_token) {
      setState(DEFAULT_STATE);
      return;
    }

    if (autoActivate) {
      void activateCurrentDevice(false);
      return;
    }

    void refresh();
  }, [activateCurrentDevice, autoActivate, refresh, session?.access_token, user]);

  useEffect(() => {
    const handleRefresh = () => {
      if (autoActivate) {
        void activateCurrentDevice(false);
        return;
      }

      void refresh();
    };

    window.addEventListener('mapleos:license-activations-refresh', handleRefresh);
    return () => window.removeEventListener('mapleos:license-activations-refresh', handleRefresh);
  }, [activateCurrentDevice, autoActivate, refresh]);

  const primaryLicense = useMemo(
    () => state.licenses.find((license) => license.id === state.primary_license_id) ?? null,
    [state.licenses, state.primary_license_id]
  );

  return {
    licenses: state.licenses,
    primaryLicense,
    primaryLicenseId: state.primary_license_id,
    currentDeviceActivationId: state.current_device_activation_id,
    currentDeviceActivated: state.current_device_activated,
    limitReached: state.limit_reached,
    message: state.message,
    loading,
    actionLoading,
    revokingId,
    refresh,
    activateCurrentDevice,
    revokeActivation,
  };
}